import { connect } from 'react-redux';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

import { openGenericModal } from '@actions/modal';

import { Button } from '@commons/utils/styledLibraryComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';

import { getClientInfo } from '@selectors/client';

import { buildOrderHeaderSelection } from '../../utils/buildOrderHeaderSelection';
import { buildOrderProductLines } from '../../utils/buildOrderProductLines';
import { buildProductSearchMatcher } from '../../utils/buildProductSearchMatcher';
import { exportExternalOrder } from '../../utils/export';
import {
  getLeaveOrderFormModal,
  getRevertToDraftModal,
  getStartPreparationModal,
} from '../../utils/getExternalOrderModals';
import { getPostDraftExternalOrderActions } from '../../utils/getPostDraftExternalOrderActions';
import { groupProductLinesByCategory } from '../../utils/groupProductLinesByCategory';
import { useCollapsedCategories } from '../../hooks/useCollapsedCategories';
import { useOrderDeliveryEdition } from '../../hooks/useOrderDeliveryEdition';
import { useOrderPendingComment } from '../../hooks/useOrderPendingComment';
import OrderClientPanel from '../OrderClientPanel';
import OrderHeaderSelectors from '../OrderHeaderSelectors';
import OrderTableHeader from '../OrderTableHeader';
import OrderTopBar from '../OrderTopBar';

import { Content, FormContainer, SelectorsBar, TableCard } from '../orderFormStyledComponents';

import OrderProductTable from './components/OrderProductTable';

/**
 * Order at the `A venir` status: the whole order is frozen but its comment, which is the only field
 * the status still lets go of. The header fields are the very same as on the draft form, disabled,
 * and the table displays the ordered lines with the VAT rate and the total excluding taxes this
 * status adds, grouped by category like the draft table.
 *
 * The saves and the revert to draft close the order and land back on the list; starting the
 * preparation stays on the form (COR-1830), which reopens on the `En préparation` view. A pending
 * comment travels with the status transitions rather than being dropped on the way.
 */
export const ValidatedExternalOrderForm = (props) => {
  const {
    externalOrder,
    canEditToComeAndInPreparation,
    canRevertToDraft,
    isSaving,
    onSaveComment,
    onSaveDelivery,
    onRevertToDraft,
    onStartPreparation,
    onClose,
    // From Redux
    stores,
    storeName,
    channels,
    hasMultipleChannels,
    currency,
    openModal,
  } = props;

  const { comment, setComment, hasCommentChange, getPendingComment } =
    useOrderPendingComment(externalOrder);

  const {
    isEditingDelivery,
    editedDeliveryDate,
    editedDeliveryTime,
    deliveryPayload,
    hasDeliveryChange,
    isMissingDeliveryDate,
    getPendingDelivery,
    startDeliveryEdition,
    stopDeliveryEdition,
    handleChangeDeliveryDate,
    handleChangeDeliveryTime,
  } = useOrderDeliveryEdition(externalOrder);

  const [search, setSearch] = useState('');

  const isCommentReadOnly = !canEditToComeAndInPreparation;

  const hasUnsavedChanges = hasCommentChange || hasDeliveryChange;

  const translatedStoreName = getClientStoreNameTranslation(storeName);

  const {
    selectedStore,
    selectableStores,
    isChannelDisplayed,
    selectableChannels,
    selectedChannel,
    deliveryDate,
    deliveryTime,
    externalClient,
    selectableExternalClients,
  } = useMemo(
    () => buildOrderHeaderSelection(externalOrder, { stores, channels, hasMultipleChannels }),
    [externalOrder, stores, channels, hasMultipleChannels],
  );

  const productLines = useMemo(
    () => buildOrderProductLines(externalOrder.products),
    [externalOrder],
  );

  // The search narrows the table only: the line count and the total describe the whole order.
  const displayedProductLines = useMemo(
    () => productLines.filter(buildProductSearchMatcher(search)),
    [productLines, search],
  );

  const categoryGroups = useMemo(
    () => groupProductLinesByCategory(displayedProductLines),
    [displayedProductLines],
  );

  const { collapsedCategories, toggleCategory } = useCollapsedCategories();

  const totalWithTaxes = useMemo(
    () => productLines.reduce((total, productLine) => total + productLine.totalWithTaxes, 0),
    [productLines],
  );

  // Nothing is edited but the comment and the delivery, so an untouched order closes straight away.
  const handleClose = () => {
    if (!hasUnsavedChanges) {
      onClose();

      return;
    }

    openModal(getLeaveOrderFormModal({ onConfirm: onClose }));
  };

  /**
   * A moved delivery closes the page through its dedicated route, the pending comment travelling
   * with it. An untouched edition is simply dropped, the comment save keeping its own way out.
   */
  const handleSave = () => {
    if (isEditingDelivery && hasDeliveryChange) {
      onSaveDelivery({ delivery: deliveryPayload, pendingComment: getPendingComment() });

      return;
    }

    if (isEditingDelivery) {
      stopDeliveryEdition();
    }

    if (hasCommentChange) {
      onSaveComment(comment);
    }
  };

  const handleRevertToDraft = useCallback(() => {
    openModal(
      getRevertToDraftModal({
        onConfirm: () => {
          onRevertToDraft(getPendingComment());
        },
      }),
    );
  }, [openModal, onRevertToDraft, getPendingComment]);

  const handleStartPreparation = () => {
    openModal(
      getStartPreparationModal({
        onConfirm: () => {
          onStartPreparation({
            pendingComment: getPendingComment(),
            delivery: getPendingDelivery(),
          });
        },
      }),
    );
  };

  // The export carries the whole order (COR-1687), the search only ever narrowing the table.
  const handleExport = useCallback(() => {
    exportExternalOrder({
      reference: externalOrder.reference,
      clientStoreName: storeName,
      storeName: selectedStore?.name,
      externalClientName: externalClient?.name,
      externalClientReference: externalClient?.externalReference,
      deliveryDate,
      createdAt: externalOrder.createdAt,
      productLines,
      currency,
    });
  }, [
    externalOrder,
    storeName,
    selectedStore,
    externalClient,
    deliveryDate,
    productLines,
    currency,
  ]);

  const actions = useMemo(
    () =>
      getPostDraftExternalOrderActions({
        revertAction: canRevertToDraft
          ? {
              id: 'action-revert-order-to-draft',
              label: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT'),
              handleClick: handleRevertToDraft,
            }
          : null,
        onEditDelivery: canEditToComeAndInPreparation ? startDeliveryEdition : null,
        onExport: handleExport,
      }),
    [
      canRevertToDraft,
      canEditToComeAndInPreparation,
      startDeliveryEdition,
      handleExport,
      handleRevertToDraft,
    ],
  );

  // The export always stands in the menu: it only collapses into an icon button when alone.
  const hasActionsMenu = actions.length > 1;

  return (
    <FormContainer>
      <OrderTopBar
        externalOrderId={externalOrder.id}
        reference={externalOrder.reference}
        status={externalOrder.status}
        updatedAt={externalOrder.updatedAt}
        isSaved
      >
        <Button
          color={'inpulse-grey'}
          handleClick={handleClose}
          icon={'/images/inpulse/close-white-small.svg'}
        />
        {!isCommentReadOnly && (
          <Button
            color={'inpulse-default'}
            handleClick={handleSave}
            icon={'/images/inpulse/save-white-small.svg'}
            isDisabled={!hasUnsavedChanges || isMissingDeliveryDate || isSaving}
          />
        )}
        {canEditToComeAndInPreparation && (
          <Button
            color={'inpulse-default'}
            handleClick={handleStartPreparation}
            icon={'/images/inpulse/check-white-small.svg'}
            isDisabled={isSaving || isMissingDeliveryDate}
            label={i18next.t('EXTERNAL_ORDERS.FORM.START_PREPARATION')}
          />
        )}
      </OrderTopBar>

      <SelectorsBar>
        <OrderHeaderSelectors
          channels={selectableChannels}
          deliveryDate={isEditingDelivery ? editedDeliveryDate : deliveryDate}
          deliveryTime={isEditingDelivery ? editedDeliveryTime : deliveryTime}
          externalClients={selectableExternalClients}
          isChannelDisplayed={isChannelDisplayed}
          isDeliveryEditable={isEditingDelivery}
          selectedChannel={selectedChannel}
          selectedExternalClient={externalClient}
          selectedStore={selectedStore}
          storeName={translatedStoreName}
          stores={selectableStores}
          isReadOnly
          onChangeDeliveryDate={handleChangeDeliveryDate}
          onChangeDeliveryTime={handleChangeDeliveryTime}
        />
        {/* Without any gated action left, the export stands alone as an icon button. */}
        {hasActionsMenu && (
          <Button
            actions={actions}
            color={'inpulse-outline'}
            iconDropdown={'/images/inpulse/ellipsis-black-small.svg'}
          />
        )}
        {!hasActionsMenu && (
          <Button
            color={'inpulse-outline'}
            handleClick={handleExport}
            icon={'/images/inpulse/file-download-black-small.svg'}
          />
        )}
      </SelectorsBar>

      <Content>
        <TableCard>
          <OrderTableHeader
            lineCount={productLines.length}
            search={search}
            totalWithTaxes={totalWithTaxes}
            onSearchChange={setSearch}
          />
          <OrderProductTable
            categoryGroups={categoryGroups}
            collapsedCategories={collapsedCategories}
            hasSearch={!!search}
            onToggleCategory={toggleCategory}
          />
        </TableCard>
        <OrderClientPanel
          comment={comment}
          externalClient={externalClient}
          isCommentReadOnly={isCommentReadOnly}
          onChangeComment={setComment}
        />
      </Content>
    </FormContainer>
  );
};

ValidatedExternalOrderForm.propTypes = {
  externalOrder: PropTypes.shape({
    id: PropTypes.string,
    reference: PropTypes.string,
    status: PropTypes.string,
    storeId: PropTypes.string,
    store: PropTypes.object,
    externalClient: PropTypes.object,
    channelId: PropTypes.string,
    deliveryDate: PropTypes.string,
    deliveryTime: PropTypes.string,
    comment: PropTypes.string,
    updatedAt: PropTypes.string,
    products: PropTypes.array,
  }).isRequired,
  canEditToComeAndInPreparation: PropTypes.bool.isRequired,
  canRevertToDraft: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onSaveComment: PropTypes.func.isRequired,
  onSaveDelivery: PropTypes.func.isRequired,
  onRevertToDraft: PropTypes.func.isRequired,
  onStartPreparation: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { storeName, clientChannels, hasMultipleChannels } = getClientInfo(state.baseReducer.user);

  return {
    stores: state.baseReducer.activeStores,
    storeName,
    channels: clientChannels,
    hasMultipleChannels,
    currency: state.baseReducer.currency,
  };
};

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ValidatedExternalOrderForm);
